/**
 * Admin Booking Configuration Validation Helpers
 * Provides client-side validation before API submission.
 */

import {
  VALIDATION_ERRORS,
  DAILY_BOOKING_LIMITS,
  MIN_ACTIVE_ADMINS_REQUIRED,
} from "../constants/adminBookingConstants";

/**
 * Validates that at least one admin remains active
 * Prevents system deadlock where no admins can receive bookings
 * 
 * @param {Array<Object>} adminConfigs - Admin configuration objects
 * @returns {Object} { isValid: boolean, error?: string }
 */
export const validateActiveAdminExists = (adminConfigs) => {
  if (!Array.isArray(adminConfigs) || adminConfigs.length === 0) {
    return {
      isValid: false,
      error: VALIDATION_ERRORS.NO_ACTIVE_ADMINS,
    };
  }

  const activeAdminCount = adminConfigs.filter((config) => config.isActive === true).length;

  if (activeAdminCount < MIN_ACTIVE_ADMINS_REQUIRED) {
    return {
      isValid: false,
      error: VALIDATION_ERRORS.NO_ACTIVE_ADMINS,
    };
  }

  return { isValid: true };
};

/**
 * Validates that inactive admins don't have high priority enabled
 * Ensures logical consistency: high priority only applies to active admins
 * 
 * @param {Array<Object>} adminConfigs - Admin configuration objects
 * @returns {Object} { isValid: boolean, error?: string, configIds?: Array<number> }
 */
export const validateInactiveAdminsNoPriority = (adminConfigs) => {
  if (!Array.isArray(adminConfigs)) {
    return {
      isValid: false,
      error: VALIDATION_ERRORS.INACTIVE_WITH_PRIORITY,
      configIds: [],
    };
  }

  const inactiveWithPriority = adminConfigs.filter(
    (config) => config.isActive === false && config.isHighPriority === true
  );

  if (inactiveWithPriority.length > 0) {
    const adminNames = inactiveWithPriority.map(
      (config) => config.adminName || `Config #${config.id}`
    );
    const configIds = inactiveWithPriority.map((config) => config.id);

    return {
      isValid: false,
      error: `${VALIDATION_ERRORS.INACTIVE_WITH_PRIORITY} Found: ${adminNames.join(", ")}.`,
      configIds,
    };
  }

  return { isValid: true, configIds: [] };
};

/**
 * Validates daily booking limit is within acceptable range
 * Ensures limits are positive integers within defined bounds
 * 
 * @param {number|string} dailyLimit - Daily booking limit value
 * @returns {Object} { isValid: boolean, error?: string }
 */
export const validateDailyBookingLimit = (dailyLimit) => {
  const limit = parseInt(dailyLimit, 10);

  if (isNaN(limit) || limit < DAILY_BOOKING_LIMITS.MIN || limit > DAILY_BOOKING_LIMITS.MAX) {
    return {
      isValid: false,
      error: VALIDATION_ERRORS.INVALID_DAILY_LIMIT,
    };
  }

  return { isValid: true };
};

/**
 * Validates all admin configurations at once
 * Runs all validation rules and returns combined results
 * 
 * @param {Array<Object>} adminConfigs - Admin configuration objects
 * @returns {Object} { isValid: boolean, errors?: Array<string> }
 */
export const validateAllAdminConfigs = (adminConfigs) => {
  const errors = [];

  const activeValidation = validateActiveAdminExists(adminConfigs);
  if (!activeValidation.isValid) {
    errors.push(activeValidation.error);
  }

  const priorityValidation = validateInactiveAdminsNoPriority(adminConfigs);
  if (!priorityValidation.isValid) {
    errors.push(priorityValidation.error);
  }

  adminConfigs.forEach((config) => {
    if (config.maxDailyLimit !== undefined && config.maxDailyLimit !== null) {
      const limitValidation = validateDailyBookingLimit(config.maxDailyLimit);
      if (!limitValidation.isValid) {
        errors.push(`Config ${config.adminName || config.id}: ${limitValidation.error}`);
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors: errors.length > 0 ? errors : undefined,
  };
};

